import { UseQueryResult } from "@tanstack/react-query";
import React from "react";

import { PageLoader } from "@/components/PageLoader";
import { getJobTrackingData, JobTrackingData } from "../queries/job-tracking-data";
import { withJob, WithJobProps } from "./withJob";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";

export type JobTrackerRouteParams = "jobNumber";

export interface WithJobTrackingDataQueryProps extends WithJobProps {
  trackingDataQuery: UseQueryResult<JobTrackingData>;
}

export function getJobTrackingDataQueryKey(jobNumber: string) {
  return ["JobTracking", jobNumber];
}

/**
 * Higher-Order Component to fetch job tracking data. The tracking data is passed to the wrapped component, which
 * must choose how to display a loading state while the data is loading.
 *
 * This component requires the job to be loaded and passed as a prop (i.e. with the `withJob` HoC) before it can fetch
 * the tracking data. The job is also passed through to the wrapped component.
 */
export const withJobTrackingDataQuery = <P extends object>(
  WrappedComponent: React.FC<WithJobTrackingDataQueryProps & P>,
): React.FC<P> => {
  // Inner component that receives job and P, injects trackingDataQuery
  const WithTrackingData: React.FC<WithJobProps & P> = (props) => {
    const { job, ...rest } = props;
    const trackingDataQuery = useTrackedQuery<JobTrackingData>({
      queryKey: getJobTrackingDataQueryKey(job.number),
      queryFn: async () => await getJobTrackingData(job),
      retry: false,
    });
    return <WrappedComponent trackingDataQuery={trackingDataQuery} job={job} {...(rest as P)} />;
  };

  // Compose withJob, return a component that only accepts P
  const WithJobHOC = withJob(WithTrackingData);
  const Result: React.FC<P> = (props) => <WithJobHOC {...props} />;
  Result.displayName = `withJobTrackingDataQuery(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return Result;
};

/**
 * Higher-Order Component to fetch job tracking data. A loading page is rendered while the tracking data is
 * loading. The wrapped component is only rendered when the tracking data is fully loaded, which is passed as a prop to
 * the wrapped component.
 *
 * This component requires the job to be loaded and passed as a prop (i.e. with the `withJob` HoC) before it can fetch
 * the tracking data. The job is also passed through to the wrapped component.
 */
export const withJobTrackingData = <P extends object>(WrappedComponent: React.FC<JobTrackingData & P>): React.FC<P> => {
  // Inner component that receives trackingDataQuery and P, injects loaded data
  const WithLoadedTrackingData: React.FC<WithJobTrackingDataQueryProps & P> = (props) => {
    const { trackingDataQuery, ...rest } = props;
    if (trackingDataQuery.isError) throw trackingDataQuery.error;
    if (trackingDataQuery.isPending) return <PageLoader text="Loading Job Data" />;
    return <WrappedComponent {...trackingDataQuery.data} {...(rest as P)} />;
  };

  // Compose withJobTrackingDataQuery, return a component that only accepts P
  const WithTrackingDataQueryHOC = withJobTrackingDataQuery(WithLoadedTrackingData);
  const Result: React.FC<P> = (props) => <WithTrackingDataQueryHOC {...props} />;
  Result.displayName = `withJobTrackingData(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return Result;
};
